import { IPaymentOfferModel } from "@nf/common";
import { ROUTES } from "@nf/constants";
import { Trans, useTranslation } from "next-i18next";
import { useCallback, useEffect, useState } from "react";
import cx from "classnames";

import { Divider, LinkWithLocale, OfferCard } from "components";
import Button from "../../../../components/Button";
import {
  useBuildLocalizedAbsolutedUrl,
  useIsAuthenticated,
  useOffers,
} from "hooks";
import { useCreatePaymentOrder } from "../../hooks/useCreatePaymentOrder";
import { PaymentError } from "../PaymentError";
import PaymentHeader from "../PaymentHeader";
import PaymentConstrainedWidth from "../PaymentConstrainedWidth";

import styles from "./ChooseSubscription.module.scss";

export const ChooseSubscription = () => {
  const { isRegisteredViaCampaign } = useIsAuthenticated();
  const { t } = useTranslation("translations");

  const [selectedOffer, setSelectedOffer] = useState<IPaymentOfferModel | null>(
    null
  );
  const { offers } = useOffers();
  const hasOffers = !!offers?.length;

  const buildLocalizedAbsolutedUrl = useBuildLocalizedAbsolutedUrl();
  const {
    mutateAsync: createPaymentOrder,
    isLoading: createPaymentOrderLoading,
    error: createPaymentOrderError,
  } = useCreatePaymentOrder();

  const selectOffer = useCallback(
    (offer: IPaymentOfferModel) => setSelectedOffer(offer),
    [setSelectedOffer]
  );

  const goToCheckout = async () => {
    if (!selectedOffer?.Id || !selectedOffer?.Handle) {
      return;
    }

    try {
      const paymentOrder = await createPaymentOrder({
        OfferId: selectedOffer.Id.toString(),
        OfferHandle: selectedOffer.Handle,
        AcceptUrl: buildLocalizedAbsolutedUrl(
          ROUTES.ASSIGNING_SUBSCRIPTION_SCREEN
        ),
        CancelUrl: buildLocalizedAbsolutedUrl(ROUTES.PAYMENT_SUBSCRIPTION),
      });

      if (paymentOrder?.CheckoutUrl) {
        window.location.href = paymentOrder.CheckoutUrl;
      }
    } catch {}
  };

  const buttonState = createPaymentOrderLoading
    ? "loading"
    : !selectedOffer
    ? "disabled"
    : "default";
  const hasTwoOffers = !!(offers?.length && offers?.length === 2);

  const confirmButtonLabel =
    selectedOffer?.TrialPeriodCount && selectedOffer?.TrialPeriodUnit
      ? t(
          `web_signup_select_subscription_trial_confirm_button_${selectedOffer.TrialPeriodUnit}`,
          { count: selectedOffer.TrialPeriodCount }
        )
      : t("web_payment_select_subscription_button");

  useEffect(() => {
    const defaultOffer = offers?.find((offer) => offer.Default);
    if (defaultOffer) {
      selectOffer(defaultOffer);
    }
  }, [offers, selectOffer]);

  return (
    <div className={styles.container}>
      <PaymentConstrainedWidth>
        <PaymentHeader />
      </PaymentConstrainedWidth>

      <PaymentConstrainedWidth
        className={cx(styles.offers, {
          [styles.wide]: !hasTwoOffers,
        })}
      >
        {offers?.map((offer) => (
          <OfferCard
            key={offer.Id}
            offer={offer}
            active={offer.Id === selectedOffer?.Id}
            wide={hasTwoOffers}
            handleSelect={selectOffer}
          />
        ))}
      </PaymentConstrainedWidth>

      <PaymentConstrainedWidth>
        <div className={styles.description}>
          {!!offers?.length
            ? t("web_payment_choose_subscription_description")
            : t("web_payment_choose_subscription_error")}
        </div>

        {createPaymentOrderError && (
          <div className="mt-md mb-md">
            <PaymentError
              error={createPaymentOrderError.Message?.toString() || ""}
            />
          </div>
        )}

        <Button
          size="medium"
          variant="primary"
          state={buttonState}
          className={styles.button}
          onClick={goToCheckout}
        >
          {confirmButtonLabel}
        </Button>

        <Divider className={styles.divider} />

        {hasOffers && !isRegisteredViaCampaign && (
          <LinkWithLocale href={ROUTES.PAYMENT_VOUCHER}>
            <a>
              <span className={styles.subtitle}>
                <Trans
                  t={t}
                  i18nKey={"web_offer_voucher_header"}
                  components={{
                    bold: <span />,
                  }}
                />
              </span>
            </a>
          </LinkWithLocale>
        )}
      </PaymentConstrainedWidth>
    </div>
  );
};

export default ChooseSubscription;
